/// <amd-module name="Artifacts/Feed/redux/Reducer" />

import {
  ArtifactsSettingsActions,
  ArtifactsSettingsActionTypes,
  CreateFeedActions,
  CreateFeedActionTypes,
  FeedHubActions,
  FeedHubActionTypes,
  IFakeDeletePackagePayload,
  IFakeListUnlistPackagePayload,
  IFakePromotePackagePayload,
  ILoadCreateFeedParamsPayload,
  IPackageListLoadCompletedPayload,
  IProblemPackageListLoadCompletedPayload,
} from "Artifacts/Feed/redux/Actions";
import {
  IArtifactsSettingsPanelState,
  ICreateFeedPanelState,
  IFeedHubState,
} from "Artifacts/Feed/redux/Contracts";
import { IArtifactsSettingsData } from "Artifacts/Feed/services/ArtifactsSettingsService";
import {
  LoadingComponentState,
  ReadyComponentState,
} from "Artifacts/Root/helpers/ComponentStateHelpers";
import { syncComponentState } from "Artifacts/Root/Redux";
import produce from "immer";

const initialHubState = (): IFeedHubState => ({
  packageListComponentState: LoadingComponentState(),
  problemPackageListComponentState: LoadingComponentState(),
});

export const feedHubReducer = produce((draft: IFeedHubState, action: FeedHubActions) => {
  syncComponentState(draft, "packageListComponentState", action, {
    ready: FeedHubActionTypes.LoadPackageListCompleted,
    loading: FeedHubActionTypes.LoadPackageListStarted,
    error: FeedHubActionTypes.LoadPackageListFailed,
  });
  syncComponentState(draft, "problemPackageListComponentState", action, {
    ready: FeedHubActionTypes.LoadProblemPackageListCompleted,
    loading: FeedHubActionTypes.LoadProblemPackageListStarted,
    error: FeedHubActionTypes.LoadProblemPackageListFailed,
  });
  switch (action.type) {
    case FeedHubActionTypes.LoadPackageListCompleted:
      draft.packageData = (action.payload as IPackageListLoadCompletedPayload).packageData;
      break;
    case FeedHubActionTypes.PackageListAmendPackages:
      draft.packageData = [
        ...(draft.packageData || []),
        ...(action.payload as IPackageListLoadCompletedPayload).packageData,
      ];
      break;
    case FeedHubActionTypes.LoadProblemPackageListCompleted:
      draft.problemPackageData = (action.payload as IProblemPackageListLoadCompletedPayload).packageData;
      break;
    case FeedHubActionTypes.FakePackagePromote:
      fakePackagePromote(draft, action.payload as IFakePromotePackagePayload);
      break;
    case FeedHubActionTypes.FakePackageDelete:
      fakePackageDelete(draft, action.payload as IFakeDeletePackagePayload);
      break;
    case FeedHubActionTypes.FakePackageListUnlist:
      fakePackageListUnlist(draft, action.payload as IFakeListUnlistPackagePayload);
      break;
  }
  return draft;
}, initialHubState());

function fakePackagePromote(draft: IFeedHubState, payload: IFakePromotePackagePayload): void {
  payload.selectedVersions.forEach((sv) => {
    const packageItem = draft.packageData?.find(
      (p) =>
        p.package.name === sv.name && p.latestVersion.normalizedVersion === sv.normalizedVersion,
    );
    if (packageItem && packageItem.latestVersion.views.indexOf(payload.viewName) < 0) {
      packageItem.latestVersion.views.push(payload.viewName);
    }
  });
}

function fakePackageDelete(draft: IFeedHubState, payload: IFakeDeletePackagePayload): void {
  payload.selectedVersions.forEach((sv) => {
    const packageItem = draft.packageData?.find((p) => p.package.id === sv.packageId);
    if (packageItem) {
      packageItem.latestVersion.isDeleted = true;
    }
  });
}

function fakePackageListUnlist(draft: IFeedHubState, payload: IFakeListUnlistPackagePayload): void {
  payload.selectedVersions.forEach((sv) => {
    const packageItem = draft.packageData?.find(
      (p) => p.package.name === sv.name && p.latestVersion.normalizedVersion === sv.version,
    );
    if (packageItem) {
      packageItem.latestVersion.isListed = payload.isListed;
    }
  });
}

const initialCreateFeedPanelState = (): ICreateFeedPanelState => ({
  componentState: LoadingComponentState(),
  createButtonComponentState: ReadyComponentState(),
});

export const createFeedPanelReducer = produce(
  (draft: ICreateFeedPanelState, action: CreateFeedActions) => {
    syncComponentState(draft, "componentState", action, {
      loading: CreateFeedActionTypes.LoadCreateFeedParamsStarted,
      ready: CreateFeedActionTypes.LoadCreateFeedParamsCompleted,
      error: CreateFeedActionTypes.LoadCreateFeedParamsFailed,
    });
    syncComponentState(draft, "createButtonComponentState", action, {
      loading: CreateFeedActionTypes.CreateFeedStarted,
      ready: CreateFeedActionTypes.CreateFeedCompleted,
      error: CreateFeedActionTypes.CreateFeedFailed,
    });
    switch (action.type) {
      case CreateFeedActionTypes.LoadCreateFeedParamsCompleted:
        draft.serviceParams = (action.payload as ILoadCreateFeedParamsPayload).createFeedServiceParams;
        break;
    }
    return draft;
  },
  initialCreateFeedPanelState(),
);

const initialArtifactsSettingsPanelState = (): IArtifactsSettingsPanelState => ({
  componentState: LoadingComponentState(),
  saveButtonComponentState: ReadyComponentState(),
});

export const artifactsSettingsPanelReducer = produce(
  (draft: IArtifactsSettingsPanelState, action: ArtifactsSettingsActions) => {
    syncComponentState(draft, "componentState", action, {
      loading: ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsStarted,
      ready: ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsCompleted,
      error: ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsFailed,
    });
    syncComponentState(draft, "saveButtonComponentState", action, {
      loading: ArtifactsSettingsActionTypes.SaveArtifactsSettingsStarted,
      ready: ArtifactsSettingsActionTypes.SaveArtifactsSettingsCompleted,
      error: ArtifactsSettingsActionTypes.SaveArtifactsSettingsFailed,
    });
    switch (action.type) {
      case ArtifactsSettingsActionTypes.LoadArtifactsSettingsParamsCompleted:
        draft.serviceParams = action.payload as IArtifactsSettingsData;
        break;
    }
    return draft;
  },
  initialArtifactsSettingsPanelState(),
);
